import React from 'react';

interface NumberPadProps {
  onNumber: (n: number) => void;
  onDecimal?: () => void;
  onClear: () => void;
  onSubmit: () => void;
  showDecimal?: boolean;
  hasDecimal?: boolean; // Whether input already has a decimal point
}

export const NumberPad: React.FC<NumberPadProps> = ({
  onNumber,
  onDecimal,
  onClear,
  onSubmit,
  showDecimal = false,
  hasDecimal = false,
}) => {
  // Fire on pointer DOWN (the instant the finger lands) instead of click, which
  // only fires on release and is cancelled by the slightest drag — that lag /
  // missed taps is what makes a tapped keypad feel unresponsive. preventDefault
  // stops focus, scrolling and the synthetic click that would double-fire.
  const press = (fn: () => void) => (e: React.PointerEvent) => {
    e.preventDefault();
    fn();
  };

  // touch-manipulation removes double-tap-zoom delay; select-none + transparent
  // tap highlight stop the grey flash / text selection from rapid tapping.
  const buttonBase =
    'rounded-2xl font-bold select-none touch-manipulation [-webkit-tap-highlight-color:transparent] ' +
    'shadow-[0_4px_0_#e5e7eb] active:translate-y-1 active:shadow-none transition-transform';

  return (
    <div className="grid grid-cols-4 gap-2 max-w-md mx-auto h-[32vh] min-h-[190px] max-h-[300px]">
      {/* Row 1: 1 2 3 Clear */}
      {[1, 2, 3].map((n) => (
        <button
          key={n}
          onPointerDown={press(() => onNumber(n))}
          className={`${buttonBase} bg-gray-50 text-3xl text-gray-600`}
        >
          {n}
        </button>
      ))}
      <button
        onPointerDown={press(onClear)}
        className={`${buttonBase} bg-red-50 text-lg text-red-500 shadow-[0_4px_0_#fee2e2]`}
      >
        けす
      </button>

      {/* Row 2: 4 5 6 */}
      {[4, 5, 6].map((n) => (
        <button
          key={n}
          onPointerDown={press(() => onNumber(n))}
          className={`${buttonBase} bg-gray-50 text-3xl text-gray-600`}
        >
          {n}
        </button>
      ))}
      <div /> {/* Empty cell */}

      {/* Row 3: 7 8 9 */}
      {[7, 8, 9].map((n) => (
        <button
          key={n}
          onPointerDown={press(() => onNumber(n))}
          className={`${buttonBase} bg-gray-50 text-3xl text-gray-600`}
        >
          {n}
        </button>
      ))}
      <div /> {/* Empty cell */}

      {/* Row 4: Decimal / 0 / Submit */}
      {showDecimal ? (
        <button
          onPointerDown={hasDecimal ? undefined : press(() => onDecimal?.())}
          disabled={hasDecimal}
          className={`${buttonBase} text-2xl ${
            hasDecimal
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
              : 'bg-blue-50 text-blue-600 shadow-[0_4px_0_#bfdbfe]'
          }`}
        >
          .
        </button>
      ) : (
        <div />
      )}
      <button
        onPointerDown={press(() => onNumber(0))}
        className={`${buttonBase} bg-gray-50 text-3xl text-gray-600 col-span-2`}
      >
        0
      </button>
      <button
        onPointerDown={press(onSubmit)}
        className={`${buttonBase} bg-green-500 text-xl text-white shadow-[0_4px_0_#15803d]`}
      >
        決定
      </button>
    </div>
  );
};
